import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from '../config/site';
import { SeoCollectorContext, type ResolvedSEO } from '../seo/context';
import { resolveStructuredData, type BreadcrumbEntry } from '../seo/structuredData';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
  breadcrumbs?: BreadcrumbEntry[];
  noindex?: boolean;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  schema,
  breadcrumbs,
  noindex = false,
}: SEOProps) => {
  const location = useLocation();
  const collect = useContext(SeoCollectorContext);
  const pagePath = new URL(canonical || location.pathname, SITE_URL).pathname;
  const normalizedPath = pagePath === '/' ? '/' : pagePath.replace(/\/+$/, '');
  const metadata: ResolvedSEO = {
    title,
    description,
    keywords,
    canonical: SITE_URL + normalizedPath,
    ogType,
    ogImage: ogImage ? new URL(ogImage, SITE_URL).href : DEFAULT_OG_IMAGE,
    schema: noindex ? undefined : resolveStructuredData(schema, normalizedPath, breadcrumbs),
    noindex,
  };

  if (collect) collect(metadata);

  useEffect(() => {
    document.title = title;
    const setMeta = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      const selector = `meta[${attribute}="${name}"]`;
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
      document.head.querySelectorAll(selector).forEach((duplicate) => {
        if (duplicate !== element) duplicate.remove();
      });
    };

    setMeta('description', description);
    setMeta('robots', noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large');
    if (keywords) setMeta('keywords', keywords);
    else document.head.querySelector('meta[name="keywords"]')?.remove();

    let canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', metadata.canonical);
    document.head.querySelectorAll('link[rel="canonical"]').forEach((duplicate) => {
      if (duplicate !== canonicalLink) duplicate.remove();
    });

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', metadata.canonical, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:locale', 'ja_JP', true);
    setMeta('og:image', metadata.ogImage, true);
    setMeta('og:image:alt', title, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', metadata.ogImage);
    setMeta('twitter:image:alt', title);

    const schemaId = 'bansow-seo-schema';
    let script = document.getElementById(schemaId);
    if (metadata.schema) {
      if (!script) {
        script = document.createElement('script');
        script.id = schemaId;
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(metadata.schema).replace(/</g, '\\u003c');
    } else {
      script?.remove();
    }
    return () => { document.getElementById(schemaId)?.remove(); };
  }, [title, description, keywords, ogType, schema, noindex, metadata.canonical, metadata.ogImage, metadata.schema]);
};
